import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import './Navbar.css';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropRef = useRef();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const categories = ['T-Shirt', 'Hoodie', 'Sweatshirt', 'Bag', 'Watch', 'Moneybag'];

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        {/* Logo */}
        <Link to="/" className="nav-logo">
          <img
            src="https://res.cloudinary.com/gtoqqet4/image/upload/v1786193349/LOGO_mrgxxx.jpg"
            alt="NORIX"
            className="nav-logo-img"
          />
        </Link>

        {/* Desktop Links */}
        <div className="nav-links">
          <Link to="/" className="nav-link">Home</Link>
          <div className="nav-dropdown">
            <button className="nav-link nav-drop-btn">
              Shop <span className="drop-arrow">▾</span>
            </button>
            <div className="dropdown-menu">
              <Link to="/products" className="drop-item">All Products</Link>
              <div className="drop-divider" />
              {categories.map(cat => (
                <Link key={cat} to={`/products?category=${cat}`} className="drop-item">
                  {cat}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Right Actions */}
        <div className="nav-actions">
          {/* Theme Toggle */}
          <button className="icon-btn" onClick={toggleTheme} title="Toggle theme">
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>

          {/* Cart */}
          <Link to="/cart" className="icon-btn cart-btn">
            🛒
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </Link>

          {/* User */}
          {user ? (
            <div className="user-menu" ref={dropRef}>
              <button className="user-avatar-btn" onClick={() => setDropOpen(o => !o)}>
                <div className="avatar-circle">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <span className="user-name-short">{user.name.split(' ')[0]}</span>
              </button>
              {dropOpen && (
                <div className="user-dropdown">
                  <div className="user-drop-header">
                    <strong>{user.name}</strong>
                    <span>{user.email}</span>
                  </div>
                  {user.isAdmin && (
                    <Link to="/admin" className="drop-item admin-item">⚡ Admin Dashboard</Link>
                  )}
                  <Link to="/orders" className="drop-item">📦 My Orders</Link>
                  <Link to="/profile" className="drop-item">👤 Profile</Link>
                  <button className="drop-item drop-logout" onClick={logout}>🚪 Logout</button>
                </div>
              )}
            </div>
          ) : (
            <div className="auth-btns">
              <Link to="/login" className="btn btn-outline" style={{ padding: '8px 18px', fontSize: '0.85rem' }}>Login</Link>
              <Link to="/register" className="btn btn-primary" style={{ padding: '8px 18px', fontSize: '0.85rem' }}>Sign Up</Link>
            </div>
          )}

          {/* Hamburger */}
          <button className="hamburger" onClick={() => setMobileOpen(o => !o)}>
            <span className={mobileOpen ? 'bar bar-open1' : 'bar'} />
            <span className={mobileOpen ? 'bar bar-open2' : 'bar'} />
            <span className={mobileOpen ? 'bar bar-open3' : 'bar'} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileOpen ? 'open' : ''}`}>
        <Link to="/" className="mob-link">Home</Link>
        <Link to="/products" className="mob-link">All Products</Link>
        {categories.map(cat => (
          <Link key={cat} to={`/products?category=${cat}`} className="mob-link mob-cat">{cat}</Link>
        ))}
        <div className="mob-divider" />
        {user ? (
          <>
            {user.isAdmin && <Link to="/admin" className="mob-link mob-admin">⚡ Admin</Link>}
            <Link to="/orders" className="mob-link">📦 Orders</Link>
            <Link to="/profile" className="mob-link">👤 Profile</Link>
            <button className="mob-link mob-logout" onClick={logout}>🚪 Logout</button>
          </>
        ) : (
          <>
            <Link to="/login" className="mob-link">Login</Link>
            <Link to="/register" className="mob-link">Sign Up</Link>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
